import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CashflowSnapshot, PulseMetric } from '../hooks/useOverviewData';
import { formatCurrency } from '../utils/format';

interface Props {
    revenue: PulseMetric | null;
    cashflow: CashflowSnapshot | null;
    cashflowPrevious?: CashflowSnapshot | null;
    loading: boolean;
}

/**
 * Average deal size: revenue / agreements for the selected window, with a
 * simple this-period-vs-prior-period comparison so the change is visible at a
 * glance. Both figures are derived here from numbers already fetched.
 */
const AvgDealSizeCard: React.FC<Props> = ({ revenue, cashflow, cashflowPrevious, loading }) => {
    const intl = useIntl();
    const locale = intl.locale;

    const current = useMemo(() => {
        if (!revenue || revenue.current == null || !cashflow || cashflow.agreement_count <= 0) return null;
        return revenue.current / cashflow.agreement_count;
    }, [revenue, cashflow]);

    const previous = useMemo(() => {
        if (!revenue || revenue.previous == null || !cashflowPrevious || cashflowPrevious.agreement_count <= 0) return null;
        return revenue.previous / cashflowPrevious.agreement_count;
    }, [revenue, cashflowPrevious]);

    const deltaPct = useMemo(() => {
        if (current == null || previous == null || previous <= 0) return null;
        return Math.round(((current - previous) / previous) * 1000) / 10;
    }, [current, previous]);

    const isEmpty = current == null && !loading;
    const max = Math.max(current ?? 0, previous ?? 0, 1);

    return (
        <section className="av2-card av2-avgdeal">
            <div className="av2-card__header">
                <div className="av2-card__title-wrap">
                    <h3 className="av2-card__title">
                        <FormattedMessage id="analyticsV2.avgDeal.title" defaultMessage="Average deal size" />
                    </h3>
                    <p className="av2-card__subtitle">
                        <FormattedMessage
                            id="analyticsV2.avgDeal.subtitle"
                            defaultMessage="Revenue per agreement in the selected period"
                        />
                    </p>
                </div>
            </div>
            <div className="av2-card__body">
                {loading ? (
                    <AvgDealSkeleton />
                ) : isEmpty ? (
                    <div className="av2-empty">
                        <FormattedMessage id="analyticsV2.avgDeal.empty" defaultMessage="No agreements in this period yet." />
                    </div>
                ) : (
                    <div className="av2-avgdeal__body">
                        <div className="av2-avgdeal__hero">
                            <span className="av2-avgdeal__value">{formatCurrency(current!, locale)}</span>
                            {deltaPct != null && Math.abs(deltaPct) >= 0.1 && (
                                <span className={`av2-avgdeal__delta av2-avgdeal__delta--${deltaPct > 0 ? 'up' : 'down'}`}>
                                    {deltaPct > 0 ? '↑' : '↓'} {deltaPct > 0 ? '+' : ''}
                                    {formatRate(deltaPct, locale)}%
                                </span>
                            )}
                        </div>

                        <div className="av2-avgdeal__compare">
                            <div className="av2-avgdeal__compare-row">
                                <span className="av2-avgdeal__compare-label">
                                    <FormattedMessage id="analyticsV2.salesFunnel.thisPeriod" defaultMessage="this period" />
                                </span>
                                <span className="av2-avgdeal__compare-track">
                                    <span
                                        className="av2-avgdeal__compare-fill"
                                        style={{ width: `${((current ?? 0) / max) * 100}%`, background: 'var(--av2-accent)' }}
                                    />
                                </span>
                                <span className="av2-avgdeal__compare-val">{formatCurrency(current!, locale)}</span>
                            </div>
                            {previous != null ? (
                                <div className="av2-avgdeal__compare-row">
                                    <span className="av2-avgdeal__compare-label">
                                        <FormattedMessage id="analyticsV2.salesFunnel.previousPeriod" defaultMessage="previous" />
                                    </span>
                                    <span className="av2-avgdeal__compare-track">
                                        <span
                                            className="av2-avgdeal__compare-fill av2-avgdeal__compare-fill--prev"
                                            style={{ width: `${(previous / max) * 100}%` }}
                                        />
                                    </span>
                                    <span className="av2-avgdeal__compare-val">{formatCurrency(previous, locale)}</span>
                                </div>
                            ) : (
                                <p className="av2-avgdeal__compare-note">
                                    <FormattedMessage
                                        id="analyticsV2.avgDeal.noCompare"
                                        defaultMessage="Turn on a comparison period to see the change."
                                    />
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

const AvgDealSkeleton: React.FC = () => (
    <div className="av2-avgdeal__body">
        <div className="av2-avgdeal__hero">
            <div className="av2-skel av2-skel--avgdeal-value" />
        </div>
        <div className="av2-avgdeal__compare">
            <div className="av2-skel av2-skel--avgdeal-sub" />
            <div className="av2-skel av2-skel--avgdeal-sub" />
        </div>
    </div>
);

const formatRate = (n: number, locale: string): string =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 1, minimumFractionDigits: 0 }).format(n);

export default AvgDealSizeCard;
